'use client';

import { useEffect, useRef, useState } from 'react';
import type { Map as MapLibreMap } from 'maplibre-gl';
import { MapLinks } from './MapLinks';
import { SHOP } from '@/lib/data/shop';
import { MAP_MAX_ZOOM, MAP_MIN_ZOOM } from '@/lib/data/map';
import { useInViewOnce } from '@/components/motion/useInViewOnce';

/**
 * The map, layered over an address panel that stands in until it has drawn.
 *
 * The panel is always in the document and gives the map its height. It is
 * hidden (visibility, so it leaves the tab order and the accessibility tree)
 * only once the map has actually painted map data, so there is no state in
 * which a visitor sees an empty rectangle.
 *
 * The directions links and the parking note live in a strip beneath, never
 * under the map, so they stay reachable whether or not the map works.
 *
 * Readiness is judged on rendered features, checked each time MapLibre goes
 * idle. 'idle' alone is not enough, since MapLibre is idle when the tile
 * pipeline never started; but re-checking on every idle means a slow
 * connection is picked up whenever it finishes rather than at one fixed moment.
 *
 * MapLibre is around 200kB. On wide screens it loads when this section nears
 * the viewport; on phones only when the visitor asks for it.
 */
const PHONE = '(max-width: 767px)';

export function ShopMap() {
  const container = useRef<HTMLDivElement>(null);
  const [nearViewport, setNearViewport] = useState(false);
  const [mapDrew, setMapDrew] = useState(false);
  // null until known, so the server render and the first client render agree.
  const [onPhone, setOnPhone] = useState<boolean | null>(null);
  const [asked, setAsked] = useState(false);

  useInViewOnce(container, () => setNearViewport(true), { rootMargin: '250px' });

  // On a phone the map waits to be asked for. It is the heaviest thing on the
  // site by far, the address and the directions links answer most visits, and
  // a phone on the street is the case where every second of loading counts.
  // Wider screens load it as the section nears the viewport, as before.
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') {
      const id = setTimeout(() => setOnPhone(false), 0);
      return () => clearTimeout(id);
    }
    const query = window.matchMedia(PHONE);
    const id = setTimeout(() => setOnPhone(query.matches), 0);
    const onChange = (event: MediaQueryListEvent) => setOnPhone(event.matches);
    query.addEventListener('change', onChange);
    return () => {
      clearTimeout(id);
      query.removeEventListener('change', onChange);
    };
  }, []);

  const wanted = onPhone === false ? nearViewport : asked;

  useEffect(() => {
    if (!wanted || !container.current) return;
    let map: MapLibreMap | null = null;
    let resizeObserver: ResizeObserver | null = null;
    let cancelled = false;

    // The map is the heaviest thing on the site. It waits for the page to
    // settle so the address, hours and links are interactive first.
    const start = async () => {
      try {
        const [maplibre, { paperStyle, withAbsoluteTiles }] = await Promise.all([
          import('maplibre-gl'),
          import('@/lib/map-style'),
          // Its stylesheet arrives with it, so no other page carries the 83 KB.
          import('maplibre-gl/dist/maplibre-gl.css'),
        ]);
        if (cancelled || !container.current) return;

        // MapLibre fetches and parses tiles in a worker loaded from a separate
        // module. Left to the bundler, the URL it derives for that module is
        // the page itself, so the worker starts, fails to parse HTML, and no
        // tile is ever requested, with nothing reported. The two files are
        // copied from node_modules by scripts/vendor-maplibre-worker.mjs.
        maplibre.setWorkerUrl(`${window.location.origin}/maplibre/maplibre-gl-worker.mjs`);

        map = new maplibre.Map({
          container: container.current,
          // Tile URLs must be absolute for the worker; see withAbsoluteTiles.
          style: withAbsoluteTiles(paperStyle, window.location.origin),
          center: [SHOP.lng, SHOP.lat],
          zoom: 15,
          minZoom: MAP_MIN_ZOOM,
          maxZoom: MAP_MAX_ZOOM,
          attributionControl: { compact: true },
          // The pin is the point; spinning the map is not.
          pitchWithRotate: false,
          dragRotate: false,
          touchZoomRotate: false,
        });

        const checkDrawn = () => {
          if (cancelled || !map) return;
          try {
            if (map.queryRenderedFeatures().length > 0) {
              setMapDrew(true);
              map.off('idle', checkDrawn);
            }
          } catch {
            // Style not ready yet; the next idle will try again.
          }
        };
        map.on('idle', checkDrawn);

        // Tile and style failures are logged rather than swallowed. The panel
        // simply remains, which is the correct outcome.
        map.on('error', (event) => {
          console.warn('[ShopMap]', event.error?.message ?? event);
        });

        map.addControl(new maplibre.NavigationControl({ showCompass: false }), 'bottom-right');

        if (typeof ResizeObserver !== 'undefined' && container.current) {
          resizeObserver = new ResizeObserver(() => map?.resize());
          resizeObserver.observe(container.current);
        }

        // A square brick marker rather than the default balloon pin, because
        // nothing on this site is rounded. Colour comes from the palette tokens,
        // not a literal, so the design-rule test can see it.
        const pin = document.createElement('div');
        pin.className = 'size-3.5 bg-brick shadow-[0_0_0_4px] shadow-brick/22';
        pin.setAttribute('aria-hidden', 'true');
        new maplibre.Marker({ element: pin }).setLngLat([SHOP.lng, SHOP.lat]).addTo(map);
      } catch {
        // The chunk failed to load, so the panel stays.
      }
    };
    // requestIdleCallback where it exists (not Safari), a short timer otherwise.
    const idle =
      typeof window.requestIdleCallback === 'function'
        ? window.requestIdleCallback(() => void start(), { timeout: 2000 })
        : window.setTimeout(() => void start(), 200);

    return () => {
      cancelled = true;
      if (typeof window.cancelIdleCallback === 'function') window.cancelIdleCallback(idle);
      else window.clearTimeout(idle);
      resizeObserver?.disconnect();
      map?.remove();
    };
  }, [wanted]);

  return (
    <div>
      <div className="relative bg-paper-2">
        {/* Fallback content and the map's height. Hidden with visibility once the
            map has drawn, which also removes it from the tab order. */}
        <div
          data-testid="map-panel"
          aria-hidden={mapDrew ? 'true' : undefined}
          className={`px-6 py-14 md:px-9 md:py-20 ${mapDrew ? 'invisible' : ''}`}
        >
          <p className="font-display text-[26px] leading-[1.25]">
            {SHOP.street}
            <br />
            {SHOP.town} {SHOP.postcode}
          </p>
          {onPhone && !mapDrew && (
            <button
              type="button"
              onClick={() => setAsked(true)}
              aria-busy={asked || undefined}
              disabled={asked}
              className="label-xs mt-7 border-b border-current pb-1.5 text-brick disabled:text-ink-soft"
            >
              {asked ? 'Loading the map' : 'Show the map'}
            </button>
          )}
        </div>

        {/* `inert` keeps MapLibre's zoom and attribution controls out of the tab
            order and the accessibility tree until the map is actually visible,
            after which they are real, announced controls. */}
        <div
          ref={container}
          data-testid="map-canvas"
          inert={!mapDrew}
          className={`map-layer transition-opacity duration-700 ${
            mapDrew ? 'opacity-100' : 'pointer-events-none opacity-0'
          }`}
        />

        {mapDrew && (
          <p className="label-xs pointer-events-none absolute top-5 left-5 z-10 bg-paper px-2 py-1.5 text-ink">
            {SHOP.street} · {SHOP.postcode}
          </p>
        )}
      </div>

      {/* Never under the map, so always reachable. */}
      <div className="flex flex-wrap items-center justify-between gap-x-8 gap-y-4 border-t border-ink/20 px-6 py-5 md:px-9">
        {SHOP.parkingNote && (
          <p className="text-[13px] leading-[1.7] text-ink-soft">{SHOP.parkingNote}</p>
        )}
        <MapLinks />
      </div>
    </div>
  );
}
